import React, { forwardRef, useImperativeHandle, useRef, useState, useEffect, useMemo } from 'react';
import { Box, Flex, Button, useToast } from '@chakra-ui/core';
import isEqual from 'lodash/isEqual';
import { ParameterItem, PaneHandle, PaneProps } from '../accountParameter.types';
import { GenericSettingsPane, GenericSettingsPaneHandle } from './GenericSettingsPane';
// import { FitbandConfiguration } from './FitbandConfiguration';
import { useStoreState } from '../../../../models/hooks';
import { useTranslation } from 'react-i18next';

interface IntegrationsState {
  rows: ParameterItem[];
  // fitBand: FitBand | undefined;
}

export const IntegrationsPane = forwardRef<PaneHandle, PaneProps>(({ onSave, setDirty, showActions }, ref) => {
  const toast = useToast();
  const genericPaneRef = useRef<GenericSettingsPaneHandle>(null);
  const account = useStoreState((state) => state.app.accounts);
  const { t } = useTranslation('accountParameter');
  const isModernHireEnabled = useStoreState((s) => s.app.accounts?.configuration?.modernHireEnable);
  const isHireFormEnabled = useStoreState((s) => s.app.accounts?.configuration?.hireFormEnabled);

  const initialIntegrationItemsConstant: ParameterItem[] = useMemo(() => {
    const items: ParameterItem[] = [];

    if (isModernHireEnabled) {
      items.push(
        {
          key: 'modernHireSubDomain',
          label: t('integrationsPane.competencyEvaluationSubDomain'),
          description: t('integrationsPane.competencyEvaluationSubDomainDescription'),
          value: account?.configuration?.modernHireSubDomain,
          type: 'string' as const,
          validationType: 'url' as const,
          isRequired: true,
        },
        {
          key: 'modernHireTemplateIdList',
          label: t('integrationsPane.competencyEvaluationTemplateIdList'),
          description: t('integrationsPane.competencyEvaluationTemplateIdListDescription'),
          value: account?.configuration?.modernHireTemplateIdList,
          type: 'string[]' as const,
          isRequired: true,
        },
      );
    }

    if (isHireFormEnabled) {
      items.push({
        key: 'receiveSeekerHireInfoAt',
        label: t('integrationsPane.hireFormPdfEmail'),
        description: t('integrationsPane.hireFormPdfEmailDescription'),
        value: account?.settings?.receiveSeekerHireInfoAt,
        type: 'string' as const,
        validationType: 'email' as const,
      });
    }

    return items;
  }, [account, t, isModernHireEnabled, isHireFormEnabled]);

  const initialIntegrationState = useMemo(
    () => ({
      rows: JSON.parse(JSON.stringify(initialIntegrationItemsConstant)) as ParameterItem[],
      fitBand: account?.configuration?.fitBand,
    }),
    [account, initialIntegrationItemsConstant],
  );

  const [initialState, setInitialState] = useState<IntegrationsState>(initialIntegrationState);
  const [integrationRows, setIntegrationRows] = useState<ParameterItem[]>(initialState.rows);
  // const [fitBand, setFitBand] = useState<FitBand | undefined>(initialState.fitBand);

  useEffect(() => {
    setInitialState(initialIntegrationState);
    setIntegrationRows(initialIntegrationState.rows);
    // setFitBand(initialIntegrationState.fitBand);
  }, [initialIntegrationState]);

  const [genericPaneDirty, setGenericPaneDirty] = useState(false);
  // const fitBandDirty = !isEqual(fitBand, initialState.fitBand);

  useEffect(() => {
    setDirty(genericPaneDirty);
  }, [genericPaneDirty, setDirty]);
  // }, [genericPaneDirty, fitBandDirty, setDirty]);

  const handleSaveAll = async () => {
    if (genericPaneRef.current && !genericPaneRef.current.validate()) {
      toast({ status: 'error', title: t('genericSettingsPane.pleaseFixValidationErrorsBeforeSaving'), isClosable: true });
      return;
    }

    const updates: Record<string, any> = {};
    integrationRows.forEach((row) => {
      const initialItem = initialState.rows.find((item) => item.key === row.key);
      if (initialItem && !isEqual(row.value, initialItem.value)) {
        updates[row.key] = row.value;
      }
    });

    // if (fitBandDirty) {
    //   updates.fitBand = fitBand;
    // }

    if (Object.keys(updates).length > 0) {
      const success = await onSave(updates);
      if (success) {
        setInitialState({ rows: JSON.parse(JSON.stringify(integrationRows)) });
        // setInitialState({ rows: JSON.parse(JSON.stringify(integrationRows)), fitBand });
        genericPaneRef.current?.resetDirtyState();
      }
    } else {
      toast({ status: 'info', title: t('integrationsPane.noChangesToSave'), isClosable: true });
    }
  };

  useImperativeHandle(ref, () => ({
    save: handleSaveAll,
    reset: () => {},
  }));

  const isDirty = genericPaneDirty;

  return (
    <Box>
      {showActions && isDirty && (
        <Flex justify="flex-end" my={4}>
          <Button colorScheme="blue" onClick={handleSaveAll}>
            {t('integrationsPane.save')}
          </Button>
        </Flex>
      )}
      <GenericSettingsPane
        ref={genericPaneRef}
        onSave={() => Promise.resolve(true)} // Part of composite save
        setDirty={setGenericPaneDirty}
        items={integrationRows}
        onRowsChange={setIntegrationRows}
        showInlineActions={false}
      />
      {/* <FitbandConfiguration value={fitBand} onChange={setFitBand} hideActionButton baselineValue={initialState.fitBand} /> */}
    </Box>
  );
});
